// ThemeProvider — aplica el BrandingConfig del partner al DOM.
//
// Estrategia:
//   1. Al montar, fetch a /partners/{partnerId}/branding.
//   2. Inyecta CSS variables en :root (--color-primary, --font-family-brand, etc.).
//   3. Resuelve theme_mode:
//        - 'light' | 'dark' → toggle fijo.
//        - 'auto' → usa prefers-color-scheme, escucha cambios.
//      El usuario puede overridear manualmente con toggleColorScheme.
//   4. Si el fetch falla (network, 5xx) o el partner no existe (null), cae
//      al default branding ya presente en index.css. El partner inexistente
//      se expone via `state` para que la UI muestre un error claro.
//
// Loading state: durante el primer fetch el SPA renderiza el default theme
// (ya seteado en index.css), asi que no hace falta splash screen.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { fetchBranding, ApiError, type BrandingConfig, type BrandingPalette } from '@/lib/api';

export type ColorScheme = 'light' | 'dark';

export type BrandingState =
  | { status: 'loading' }
  | { status: 'ready'; branding: BrandingConfig }
  | { status: 'default'; reason: 'not_found' | 'fetch_failed' };

interface ThemeContextValue {
  state: BrandingState;
  colorScheme: ColorScheme;
  toggleColorScheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Paleta activa según el color scheme. Punto único de verdad: la usan el
// apply de CSS variables de este provider y el tema del widget de pago
// (lineTheme.ts), así el checkout siempre hereda lo mismo que ve el portal.
export function resolvePalette(branding: BrandingConfig, scheme: ColorScheme): BrandingPalette {
  return scheme === 'dark' && branding.colors_dark ? branding.colors_dark : branding.colors;
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function applyPalette(palette: BrandingPalette): void {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', palette.primary);
  root.style.setProperty('--color-accent', palette.accent);
  root.style.setProperty('--color-background', palette.background);
  root.style.setProperty('--color-surface', palette.surface);
  root.style.setProperty('--color-text', palette.text);
  root.style.setProperty('--color-text-muted', palette.text_muted);
  root.style.setProperty('--color-error', palette.error);
  // `success` es opcional (branding pre-2026-07 no lo tiene). Sin valor, hay
  // que REMOVER la inline var — si quedara seteada pisaría el default por tema
  // de index.css (:root/.dark), que es el fallback correcto.
  if (palette.success) {
    root.style.setProperty('--color-success', palette.success);
  } else {
    root.style.removeProperty('--color-success');
  }
}

function applyFontFamily(fontFamily: string): void {
  document.documentElement.style.setProperty('--font-family-brand', fontFamily);
}

/**
 * Carga el stylesheet de una fuente custom (ej: Google Fonts) inyectando un
 * `<link rel="stylesheet">` en el `<head>`. Idempotente — si ya existe un link
 * marcado con `data-branding-font` con el mismo `href`, no hace nada. Si el
 * `href` cambio, swappea el link existente.
 *
 * El nombre real de la familia para CSS lo provee `font_family` aparte; este
 * helper solo se encarga de la descarga del archivo de fuente.
 */
function applyFontStylesheet(fontUrl: string | undefined): void {
  const existing = document.head.querySelector<HTMLLinkElement>(
    'link[data-branding-font="true"]',
  );
  if (!fontUrl) {
    existing?.remove();
    return;
  }
  if (existing && existing.href === fontUrl) return;
  if (existing) {
    existing.href = fontUrl;
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  link.setAttribute('data-branding-font', 'true');
  document.head.appendChild(link);
}

function applyColorScheme(scheme: ColorScheme): void {
  const root = document.documentElement;
  if (scheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

function applyFavicon(faviconUrl: string | undefined): void {
  if (!faviconUrl) return;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}

// ---------------------------------------------------------------------------
// System color scheme detection
// ---------------------------------------------------------------------------

function getSystemColorScheme(): ColorScheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveInitialScheme(themeMode: BrandingConfig['theme_mode'] | undefined): ColorScheme {
  if (themeMode === 'dark') return 'dark';
  if (themeMode === 'light') return 'light';
  return getSystemColorScheme();
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ThemeProviderProps {
  partnerId: string;
  children: ReactNode;
}

export function ThemeProvider({ partnerId, children }: ThemeProviderProps): JSX.Element {
  const [state, setState] = useState<BrandingState>({ status: 'loading' });
  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => getSystemColorScheme());
  // `manualOverride` = el usuario toco el toggle → ignorar cambios del sistema
  // hasta que recargue. No persistimos a localStorage en M8.2; se agrega si
  // hace falta al cerrar el feedback de diseño.
  const [manualOverride, setManualOverride] = useState(false);

  // Fetch branding.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const branding = await fetchBranding(partnerId);
        if (cancelled) return;

        if (!branding) {
          setState({ status: 'default', reason: 'not_found' });
          return;
        }

        setState({ status: 'ready', branding });
        if (!manualOverride) {
          setColorScheme(resolveInitialScheme(branding.theme_mode));
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('branding fetch failed', err instanceof ApiError ? err.code : err);
        setState({ status: 'default', reason: 'fetch_failed' });
      }
    })();

    return () => {
      cancelled = true;
    };
    // `manualOverride` intencionalmente excluido — solo afecta el primer
    // apply; re-fetchear cuando el usuario toggle-a seria wasteful.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  // Apply palette + font + favicon cuando cambia state o colorScheme.
  useEffect(() => {
    if (state.status !== 'ready') return;
    const { branding } = state;
    applyPalette(resolvePalette(branding, colorScheme));
    // Cargar stylesheet de la fuente PRIMERO — el browser inicia la descarga
    // en paralelo mientras renderizamos. Luego declaramos font-family.
    applyFontStylesheet(branding.font_url);
    applyFontFamily(branding.font_family);
    applyFavicon(branding.favicon_url);
  }, [state, colorScheme]);

  // Apply color scheme class (independiente de si branding llego).
  useEffect(() => {
    applyColorScheme(colorScheme);
  }, [colorScheme]);

  // Listener de prefers-color-scheme — solo activo si theme_mode = 'auto'
  // y el usuario no hizo override manual.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (manualOverride) return;
    if (state.status === 'ready' && state.branding.theme_mode !== 'auto') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void => {
      setColorScheme(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [state, manualOverride]);

  const toggleColorScheme = useCallback(() => {
    setManualOverride(true);
    setColorScheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ state, colorScheme, toggleColorScheme }),
    [state, colorScheme, toggleColorScheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}
